// Hulpfuncties die door meerdere rules worden gebruikt
function isPathMatching(paths, currentPath = window.location.pathname) {
  if (!paths || !Array.isArray(paths)) return false;

  try {
    return paths.some(path => {
      if (!path) return false;
      if (path === '*') return true;
      if (typeof currentPath === 'string' && typeof path === 'string') {
        return currentPath.includes(path);
      }
      return false;
    });
  } catch (error) {
    console.error('Error in path matching:', error);
    return false;
  }
}

function validatePaths(paths) {
  if (!paths || !Array.isArray(paths) || paths.length === 0) {
    return ['*'];
  }
  return paths;
}

// Rule handlers
export class PageTimeoutRule {
  constructor(controller) {
    this.controller = controller;
    this.storageKey = controller.STORAGE_KEYS.page_timeout;
  }

  process(rule, visitData) {
    const isMatchingPath = isPathMatching(rule.paths);

    if (isMatchingPath) {
      const timeoutSeconds = rule.seconds || 30;
      this.startTimeout(timeoutSeconds);
    }
  }

  startTimeout(seconds) {
    setTimeout(() => {
      localStorage.setItem(this.storageKey, 'true');
    }, seconds * 1000);
  }

  evaluate(visitData) {
    return localStorage.getItem(this.storageKey) === 'true';
  }
}

export class VisitAfterRule {
  constructor(controller) {
    this.controller = controller;
    this.storageKey = controller.STORAGE_KEYS.visit_after;
    this.timeoutKey = controller.STORAGE_KEYS.page_timeout;
  }

  process(rule, visitData) {
    const timeoutCompleted = localStorage.getItem(this.timeoutKey) === 'true';

    if (timeoutCompleted) {
      localStorage.setItem(this.storageKey, 'true');
    }
  }

  evaluate(visitData) {
    return localStorage.getItem(this.storageKey) === 'true';
  }
}

export class ScrollDepthRule {
  constructor(controller) {
    this.controller = controller;
    this.storageKey = controller.STORAGE_KEYS.scroll_depth;
    this.scrollHandler = null;
  }

  process(rule, visitData) {
    // Verwijder bestaande handler als die er is
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    const minScrollDepth = rule.depth || 50;
    this.scrollHandler = this.createScrollHandler(minScrollDepth);
    window.addEventListener('scroll', this.scrollHandler);
  }

  createScrollHandler(minScrollDepth) {
    return () => {
      const scrollPercentage = this.calculateScrollPercentage();
      if (scrollPercentage >= minScrollDepth) {
        localStorage.setItem(this.storageKey, 'true');
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    };
  }

  calculateScrollPercentage() {
    const scrollPosition = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    return (scrollPosition / documentHeight) * 100;
  }

  evaluate(visitData) {
    return localStorage.getItem(this.storageKey) === 'true';
  }

  cleanup() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
  }
}

export class PageViewsRule {
  constructor(controller) {
    this.controller = controller;
    this.storageKey = controller.STORAGE_KEYS.page_views;
    this.completedKey = `${this.storageKey}_completed`;
  }

  process(rule, visitData) {
    const currentPath = window.location.pathname || '';
    const pageViewsData = this.getOrInitPageViewsData(visitData);
    const safePaths = validatePaths(rule.paths);

    const isMatchingPath = isPathMatching(safePaths, currentPath);

    if (isMatchingPath && !pageViewsData.visitedPaths.includes(currentPath)) {
      this.updatePageViewsData(pageViewsData, currentPath, rule);
    }
  }

  getOrInitPageViewsData(visitData) {
    let data = visitData.pageViewsData || { visitedPaths: [], pathCount: 0 };

    // Ensure visitedPaths is always an array
    if (!data.visitedPaths || !Array.isArray(data.visitedPaths)) {
      data.visitedPaths = [];
      data.pathCount = 0;
    }

    return data;
  }

  updatePageViewsData(pageViewsData, currentPath, rule) {
    pageViewsData.visitedPaths.push(currentPath);
    pageViewsData.pathCount = pageViewsData.visitedPaths.length;

    localStorage.setItem(
      this.storageKey,
      JSON.stringify(pageViewsData)
    );

    const requiredCount = Math.min(rule.count || 1, (rule.paths || []).length);
    if (pageViewsData.pathCount >= requiredCount) {
      localStorage.setItem(this.completedKey, 'true');
    }
  }

  evaluate(visitData) {
    return localStorage.getItem(this.completedKey) === 'true';
  }
}

export class TimeOnSiteRule {
  constructor(controller) {
    this.controller = controller;
    this.storageKey = controller.STORAGE_KEYS.time_on_site;
    this.completedKey = `${this.storageKey}_completed`;
    this.sessionStartTime = null;
    this.trackingInterval = null;
  }

  process(rule, visitData) {
    const timeData = visitData.timeOnSiteData || { totalSeconds: 0 };
    const requiredSeconds = rule.seconds || 60;

    if (timeData.totalSeconds >= requiredSeconds) {
      localStorage.setItem(this.completedKey, 'true');
    }
  }

  initTracking() {
    const timeData = this.getOrInitTimeData();

    this.sessionStartTime = new Date();
    this.trackingInterval = setInterval(() => {
      this.saveTimeOnSite();
    }, 10000);

    window.addEventListener('beforeunload', () => {
      this.saveTimeOnSite();
    });
  }

  getOrInitTimeData() {
    let timeData;
    try {
      timeData = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
    } catch (e) {
      timeData = {};
    }

    if (!timeData.totalSeconds) {
      timeData.totalSeconds = 0;
    }
    return timeData;
  }

  saveTimeOnSite() {
    if (!this.sessionStartTime) return;

    const now = new Date();
    const sessionDuration = (now - this.sessionStartTime) / 1000;
    this.sessionStartTime = now;

    let timeData = this.getOrInitTimeData();
    timeData.totalSeconds += sessionDuration;

    localStorage.setItem(
      this.storageKey,
      JSON.stringify(timeData)
    );

    // Controleer of we de vereiste tijd hebben bereikt
    this.checkTimeOnSiteCompletion(timeData);
  }

  checkTimeOnSiteCompletion(timeData) {
    // We hebben hier de rule niet, dus we zoeken deze in de controller
    const timeOnSiteRule = this.controller.findRuleByType('time_on_site');

    if (timeOnSiteRule) {
      const requiredSeconds = timeOnSiteRule.seconds || 60;
      if (timeData.totalSeconds >= requiredSeconds) {
        localStorage.setItem(this.completedKey, 'true');
      }
    }
  }

  evaluate(visitData) {
    return localStorage.getItem(this.completedKey) === 'true';
  }

  cleanup() {
    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
      this.trackingInterval = null;
    }
  }
}

// Factory voor het maken van rule handlers
export function createRuleHandler(type, controller) {
  const handlers = {
    'page_timeout': () => new PageTimeoutRule(controller),
    'visit_after': () => new VisitAfterRule(controller),
    'scroll_depth': () => new ScrollDepthRule(controller),
    'page_views': () => new PageViewsRule(controller),
    'time_on_site': () => new TimeOnSiteRule(controller)
  };

  const handlerCreator = handlers[type];
  return handlerCreator ? handlerCreator() : null;
}